import { createContext, useContext, useState, useCallback, useEffect } from "react";
import axios from "axios";
import i18n from "../i18n";
import logger from "../lib/logger";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const AuthContext = createContext(null);

// =====================================================================
// Session handling — cookie-based (httpOnly).
// =====================================================================
// We no longer touch localStorage for the access token. The backend sets a
// session_token cookie on /auth/login, /auth/register, /auth/logout and the
// SSO callbacks (Microsoft/Google). The cookie is HttpOnly so JS cannot read
// it — XSS no longer leaks the session.
//
// Two side-channels still exist:
//   - sessionStorage("impersonation_token"): platform-admin impersonation
//     drops the impersonation JWT in a tab-scoped store and the request
//     interceptor sends it as an Authorization: Bearer header (because the
//     impersonator must be able to "switch" without re-issuing the cookie
//     and clobbering their own session). Tab-scoped means closing the tab
//     ends the impersonation.
//   - The Authorization header path is still honored server-side
//     (utils/auth.get_current_user reads both cookie and header), so an old
//     cached frontend bundle with localStorage tokens keeps working until
//     it's reloaded.
//
export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [tenant, setTenant] = useState(null);
  const [tenantConfig, setTenantConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  // El inicializador cubre el recargar una pestaña que ya venía impersonando;
  // la llegada inicial con ?impersonate=… la cubre el efecto de abajo.
  const [isImpersonating, setIsImpersonating] = useState(
    () => !!sessionStorage.getItem("impersonation_token"),
  );

  // On mount: detect ?impersonate=TOKEN in URL and store in sessionStorage (tab-scoped)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const impToken = params.get("impersonate");
    if (impToken) {
      sessionStorage.setItem("impersonation_token", impToken);
      setIsImpersonating(true);
      params.delete("impersonate");
      const newSearch = params.toString();
      window.history.replaceState({}, "", window.location.pathname + (newSearch ? `?${newSearch}` : ""));
    }
  }, []);

  const checkAuth = useCallback(async () => {
    logger.debug("[AuthContext] checkAuth: START");
    // Debe ir por la instancia `api`, no por `axios` pelado: es el request
    // interceptor de `api` el que inyecta el Bearer de impersonación. Con
    // axios pelado esta llamada viajaba solo con la cookie, así que en una
    // pestaña impersonando devolvía la identidad del platform admin (su
    // tenant_name, sus permisos, sus features) mientras el resto de la app
    // —que sí usa `api`— traía los datos del tenant impersonado.
    //
    // skipAuthRedirect: acá un 401 es el caso normal de "visita anónima"
    // (Landing llama a checkAuth), así que no debe rebotar a /login.
    try {
      const response = await api.get("/auth/me", { skipAuthRedirect: true });
      logger.debug("[AuthContext] checkAuth: /auth/me returned user", response.data.email);
      setUser(response.data);
      setTenant({ tenant_id: response.data.tenant_id, name: response.data.tenant_name });

      if (response.data.language) {
        i18n.changeLanguage(response.data.language);
        localStorage.setItem("language", response.data.language);
      }
    } catch (error) {
      // 401 here is the normal "no session" case on a fresh visit; only
      // unexpected errors are worth logging at error level.
      if (error?.response?.status !== 401) {
        logger.error("[AuthContext] checkAuth: ERROR", error);
      }
      setUser(null);
      setTenant(null);
    } finally {
      setLoading(false);
    }
    logger.debug("[AuthContext] checkAuth: END");
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    logger.debug("[AuthContext] login: START", { email });
    // The backend sets the session_token cookie in the response. We don't
    // need to read response.data.token (still returned for backwards compat
    // with old frontend bundles).
    const response = await axios.post(
      `${API}/auth/login`,
      { email, password },
      { withCredentials: true },
    );

    const { user: userData, tenant: tenantData } = response.data;
    setUser(userData);
    setTenant(tenantData);
    setLoading(false);

    if (userData.language) {
      i18n.changeLanguage(userData.language);
      localStorage.setItem("language", userData.language);
    }

    logger.debug("[AuthContext] login: SUCCESS");
    return userData;
  };

  // Paso 1 de 2 del alta. Ya NO devuelve sesión: el backend responde siempre
  // `verification_sent`, exista el correo o no, y manda un enlace por correo.
  // Esa uniformidad es el punto — antes un correo ya registrado devolvía 400 y
  // uno libre devolvía la sesión, o sea que el formulario servía para averiguar
  // quién es cliente. La cuenta se crea en verifyEmail().
  const register = async (userData, tenantData) => {
    const response = await axios.post(
      `${API}/auth/register`,
      { ...userData, ...tenantData },
      { withCredentials: true },
    );
    return { kind: "verification_sent", ...response.data };
  };

  // Paso 2 de 2: el usuario volvió desde el enlace del correo.
  //   - `registered`         → holding nuevo; el backend ya dejó la cookie de
  //                            sesión en esta respuesta, así que quedamos dentro.
  //   - `request_submitted`  → el dominio ya tenía un holding; queda una
  //                            solicitud esperando a un admin y NO hay sesión.
  const verifyEmail = async (token) => {
    const response = await axios.post(
      `${API}/auth/verify-email`,
      { token },
      { withCredentials: true },
    );

    if (response.data?.kind === "request_submitted") {
      return { kind: "request_submitted", ...response.data };
    }

    const { user: newUser, tenant: newTenant } = response.data;
    setUser(newUser);
    setTenant(newTenant);
    setLoading(false);

    return { kind: "registered", user: newUser };
  };

  const logout = async () => {
    // En una pestaña impersonada, "cerrar sesión" termina SOLO la
    // impersonación. Llamar a /auth/logout mandaría la cookie del platform
    // admin —que es la misma en todas las pestañas— y lo desconectaría de
    // todas ellas. Acá basta con soltar el token de esta pestaña: la cookie
    // propia sigue intacta, así que volvemos a /platform con la identidad real.
    if (sessionStorage.getItem("impersonation_token")) {
      sessionStorage.removeItem("impersonation_token");
      setUser(null);
      setTenant(null);
      // Recarga dura a propósito: el árbol entero (contextos y cachés de cada
      // página) se armó para el tenant impersonado. Remontar desde cero evita
      // que quede mezclado con la identidad propia.
      window.location.href = "/platform/tenants";
      return { endedImpersonation: true };
    }

    try {
      // The cookie is sent automatically via withCredentials; the backend
      // clears it in the response (clear_session_cookie).
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      logger.error("Logout error:", error);
    }
    setUser(null);
    setTenant(null);
    return { endedImpersonation: false };
  };

  const loginWithToken = async (newToken) => {
    // Hoy solo lo usa la página /sso-callback, y con newToken null (la cookie
    // ya la dejó puesta el redirect del backend). La impersonación NO pasa por
    // acá: guarda el token en el efecto de montaje y se apoya en checkAuth.
    if (newToken) {
      sessionStorage.setItem("impersonation_token", newToken);
    }

    try {
      // Por `api` para que un newToken recién guardado sí viaje en el header.
      // skipAuthRedirect: el catch de abajo ya limpia y propaga; que redirija
      // además el interceptor le quita al llamador la decisión.
      const response = await api.get("/auth/me", { skipAuthRedirect: true });
      setUser(response.data);
      setTenant({ tenant_id: response.data.tenant_id, name: response.data.tenant_name });

      if (response.data.language) {
        i18n.changeLanguage(response.data.language);
        localStorage.setItem("language", response.data.language);
      }

      setLoading(false);
      return response.data;
    } catch (error) {
      sessionStorage.removeItem("impersonation_token");
      setUser(null);
      setTenant(null);
      throw error;
    }
  };

  const changeLanguage = (lang) => {
    i18n.changeLanguage(lang);
    localStorage.setItem("language", lang);
  };

  // Fetch tenant config (regional settings, etc.) when authenticated.
  // Cached at AuthContext level so any page can read it without re-fetching.
  useEffect(() => {
    if (!user) {
      setTenantConfig(null);
      return;
    }
    api.get("/tenant/config")
      .then(res => setTenantConfig(res.data?.config || null))
      .catch(() => setTenantConfig(null));
  }, [user?.tenant_id]);

  // Check if user has a specific permission
  const hasPermission = useCallback((module, action) => {
    if (!user) return false;
    // Super admin always has all permissions
    if (user.role === "super_admin") return true;
    // Check user's permissions
    const modulePerms = user.permissions?.[module] || [];
    return modulePerms.includes(action);
  }, [user]);

  const value = {
    user,
    tenant,
    tenantConfig,
    loading,
    isImpersonating,
    login,
    loginWithToken,
    register,
    verifyEmail,
    logout,
    checkAuth,
    setUser,
    setLoading,
    setTenantConfig,
    changeLanguage,
    hasPermission,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}

// =====================================================================
// Axios instance + interceptors
// =====================================================================
export const api = axios.create({
  baseURL: API,
  withCredentials: true,
  timeout: 15000, // 15 seconds timeout
});

api.interceptors.request.use((config) => {
  // The regular session lives in the httpOnly cookie (sent by the browser
  // automatically thanks to withCredentials). The only reason to inject an
  // Authorization header is impersonation: the impersonating platform admin
  // keeps their own cookie, and we override the identity per-tab with this
  // sessionStorage token.
  const impersonationToken = sessionStorage.getItem("impersonation_token");
  if (impersonationToken) {
    config.headers.Authorization = `Bearer ${impersonationToken}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Un llamador puede pedir `skipAuthRedirect: true` cuando el 401 es un
    // estado esperado y no una sesión caída: /auth/me al arrancar responde 401
    // en toda visita anónima, y rebotar ahí sacaría al visitante del Landing.
    if (error.response?.status === 401 && !error.config?.skipAuthRedirect) {
      // The backend either has no valid cookie or the token expired. Clear
      // any in-flight impersonation state and bounce to /login. The cookie
      // itself is cleared by the next successful /auth/logout (or by its
      // own expiration).
      sessionStorage.removeItem("impersonation_token");
      window.location.href = "/login";
    }
    return Promise.reject(error);
  },
);
